import { DateField } from "@/features/FilterTable/DateField";
import {
  dateSchema,
  formatDateValueToISOString,
  parseToDateValue,
} from "@/features/FormCard/utils";
import { useCurrentPageFromUrl } from "@/hooks/useClientActions";
import { useSyncTptReception } from "@/hooks/useSyncTptReception";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { Button, Popover, PopoverContent, PopoverTrigger } from "@heroui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { Resolver, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const SyncTptReceptionSchema = z.object({
  portfolioDate: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .refine((val) => val !== null, {
      message: "form.date_required",
    })
    .describe("date"),
});

type SyncTptReceptionSchemaType = z.infer<typeof SyncTptReceptionSchema>;

type SyncTptReceptionButtonProps = {
  client: string;
};

const SYNC_TPT_RECEPTION_ACTION_NAME = "sync_tpt_reception";

export function SyncTptReceptionButton({
  client,
}: SyncTptReceptionButtonProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const currentPage = useCurrentPageFromUrl();
  const mutation = useSyncTptReception({ client });
  const actionName = t("actions.sync_tpt_reception", {
    defaultValue: "Sync TPT reception",
  });
  const {
    handleSubmit,
    setValue,
    watch,
    register,
    reset,
    formState: { isValid },
  } = useForm<SyncTptReceptionSchemaType>({
    mode: "onChange",
    defaultValues: {
      portfolioDate: undefined,
    },
    resolver: zodResolver(
      SyncTptReceptionSchema,
    ) as Resolver<SyncTptReceptionSchemaType>,
  });

  const handleFormSubmit = (data: SyncTptReceptionSchemaType) => {
    const portfolioDate = formatDateValueToISOString(data.portfolioDate);
    if (!portfolioDate) return;

    mutation.mutate(
      {
        portfolio_date: portfolioDate,
        action_button_name: SYNC_TPT_RECEPTION_ACTION_NAME,
        action_execution_page: currentPage ?? "unknown",
      },
      {
        onSuccess: () => {
          setIsOpen(false);
          reset();
        },
      },
    );
  };

  return (
    <Popover
      placement="bottom"
      showArrow
      offset={10}
      isOpen={isOpen}
      onOpenChange={setIsOpen}
    >
      <PopoverTrigger>
        <Button
          size="sm"
          radius="full"
          style={{
            border: "solid 2px transparent",
            backgroundImage: `linear-gradient(hsl(var(--heroui-background)), hsl(var(--heroui-background))), linear-gradient(83.87deg, #F54180, #9353D3)`,
            backgroundOrigin: "border-box",
            backgroundClip: "padding-box, border-box",
          }}
          endContent={<ChevronDownIcon className="h-4 w-4" />}
        >
          {actionName}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80">
        <form
          onSubmit={handleSubmit(handleFormSubmit)}
          className="space-y-4 p-1 w-full"
        >
          <DateField
            name="portfolioDate"
            label={t("form.date", { defaultValue: "Date" })}
            watch={watch}
            setValue={setValue}
            register={register}
            required={true}
          />

          <footer className="mt-4 w-full">
            <Button
              color="primary"
              radius="full"
              size="sm"
              type="submit"
              className="w-24"
              isDisabled={!isValid}
              isLoading={mutation.isPending}
            >
              {t("actions.confirm")}
            </Button>
          </footer>
        </form>
      </PopoverContent>
    </Popover>
  );
}

export function C3SyncTptReceptionButton() {
  return <SyncTptReceptionButton client="c3" />;
}

export function E2SyncTptReceptionButton() {
  return <SyncTptReceptionButton client="e2" />;
}

export function G4SyncTptReceptionButton() {
  return <SyncTptReceptionButton client="g4" />;
}

export function I1SyncTptReceptionButton() {
  return <SyncTptReceptionButton client="i1" />;
}

export function C7SyncTptReceptionButton() {
  return <SyncTptReceptionButton client="c7" />;
}
